import { useEffect, useMemo, useRef, useState } from "react";
import {
  getSelectedLabel,
  getSelectedValue,
  getValueAtrr,
  searchOptions,
} from "./utils/common";
import shortid from "shortid";
import Anchor from "./Anchor";
import "./css/TypeaheadField.css";
import Label from "./Label";
export default function TypeaheadField({
  id,
  name,
  className,
  label,
  value,
  onChange,
  onInputChange,
  options,
  valueAttr,
  labelAttr,
  emptyLabel,
  isInvalid,
  error,
  help,
  labelStyle,
  disabled,
  ...props
}) {
  const [search, setSearch] = useState("");
  const [selectedLabel, setSelectedLabel] = useState(
    getSelectedLabel(options, value, labelAttr, valueAttr, emptyLabel),
  );
  const [computedOptions, setComputedOptions] = useState([]);
  const [optionsShowing, setOptionsShowing] = useState(false);
  const searchInput = useRef();
  const inputRef = useRef();
  const optionsElement = useRef();
  const handleChange = useMemo(
    () => (event) => {
      if (event.target === inputRef.current) {
        if (onChange instanceof Function) {
          onChange(event);
        }
      } else {
        event.stopPropagation();
      }
    },
    [onChange],
  );
  const handleOptionClick = (value) => (event) => {
    event.preventDefault();
    if (!disabled) {
      const selectedValue = getSelectedValue(options, value, valueAttr);
      inputRef.current.name = name;
      inputRef.current.value = selectedValue;
      inputRef.current.dispatchEvent(
        new Event("change", {
          cancelable: true,
          bubbles: true,
          composed: false,
        }),
      );
      searchInput.current.focus();
      setOptionsShowing(false);
      setSelectedLabel(getSelectedLabel(options, value, labelAttr, valueAttr));
      setSearch("");
    }
  };
  const handleSearchKeyDown = (event) => {
    if (!disabled) {
      switch (event.keyCode) {
        case 40:
          event.preventDefault();
          if (!optionsShowing) {
            setOptionsShowing(true);
          } else {
            optionsElement.current.getElementsByTagName("a")[0].focus();
          }
          break;
        case 38:
        case 27:
          event.preventDefault();
          setOptionsShowing(false);
          break;
        default:
          break;
      }
    }
  };
  const handleOptionsKeyDown = (event) => {
    if (!disabled) {
      switch (event.keyCode) {
        case 37:
        case 27:
          event.preventDefault();
          searchInput.current.focus();
          setOptionsShowing(false);
          break;
        default:
          break;
      }
    }
  };
  const handleSearchChange = (event) => {
    event.preventDefault();
    if (event.currentTarget.value > 0 && !optionsShowing) {
      setOptionsShowing(true);
    }
    setSearch(event.currentTarget.value);
    onInputChange && onInputChange(event);
  };
  const handleCaretClick = (event) => {
    event.preventDefault();
    if (!disabled) {
      setOptionsShowing((previous) => !previous);
    }
  };
  const caretClassName = [
    "fa",
    (optionsShowing && "fa-chevron-up") || "fa-chevron-down",
  ]
    .join(" ")
    .trim();
  const computedClassName = [
    "form-group",
    "typeahead-field",
    isInvalid ? "has-error" : "",
    ...((!!className && className.split(" ")) || []),
  ]
    .join(" ")
    .trim();
  const dropdownClassName = ["dropdown", (!!optionsShowing && "open") || ""]
    .join(" ")
    .trim();
  useEffect(() => {
    searchOptions(options, search, value, labelAttr, valueAttr).then(
      (options) => setComputedOptions(options),
    );
  }, [options, labelAttr, valueAttr, search, value]);
  useEffect(() => {
    setSelectedLabel(
      getSelectedLabel(
        options,
        getValueAtrr(value, valueAttr),
        labelAttr,
        valueAttr,
      ),
    );
  }, [options, labelAttr, valueAttr, value]);
  useEffect(() => {
    if (optionsShowing) {
      const selected =
        optionsElement.current.getElementsByClassName("selected")[0];
      if (!!selected) {
        selected.scrollIntoView();
        selected.focus();
      } else {
        optionsElement.current.focus();
      }
    }
  }, [optionsShowing]);
  useEffect(() => {
    const input = inputRef.current;
    const onChange = handleChange;
    if (!!input) {
      input.addEventListener("change", onChange);
      return () => {
        input.removeEventListener("change", onChange);
      };
    }
  }, [handleChange]);
  return (
    <div id={id} className={computedClassName} {...props} ref={inputRef}>
      {!!label && (
        <Label for={`${id}_value`} style={labelStyle} className="control-label">
          {label}
        </Label>
      )}
      <div className={dropdownClassName}>
        <Anchor onClick={handleCaretClick}>
          <i className={caretClassName}></i>
        </Anchor>
        <input
          id={`${id}_value`}
          className="form-control"
          type="text"
          value={selectedLabel}
          aria-invalid={isInvalid}
          onKeyDown={handleSearchKeyDown}
          contentEditable={false}
          disabled={disabled}
        />
        <ul
          className="dropdown-menu"
          ref={optionsElement}
          aria-labelledby={`${id}_search`}
          onKeyDown={handleOptionsKeyDown}
        >
          <li className="dropdown-header">
            <input
              id={`${id}_search`}
              ref={searchInput}
              className="form-control"
              type="text"
              value={search}
              onChange={handleSearchChange}
              disabled={disabled}
            />
          </li>
          {!!emptyLabel && (
            <li>
              <Anchor
                onClick={handleOptionClick(undefined)}
                className={
                  getValueAtrr(value, valueAttr) === undefined && "selected"
                }
              >
                {emptyLabel}
              </Anchor>
            </li>
          )}
          {computedOptions.map((option, index) => (
            <li key={option.value}>
              <Anchor
                onClick={handleOptionClick(option.value)}
                className={option.value === value && "selected"}
              >
                {option.label}
              </Anchor>
            </li>
          ))}
        </ul>
      </div>
      {isInvalid && !!error && <span className="help-block">{error}</span>}
    </div>
  );
}

TypeaheadField.defaultPrps = {
  id: shortid(),
  className: "",
  onChange: (event) => console.debug(event),
};
